import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'app/components/Button';
import Dropdown from 'app/components/Dropdown';
import { formatTimer } from 'app/utils/numbers';
import InputTime from 'app/components/Input/InputTime';

import style from './style.scss';

const MICROMARKET_TIME_WINDOW_MAX_IN_S = 60 * 5;

interface Props {
  onCancel: () => void;
  disabled?: boolean;
  onlyLabel?: boolean;
  value: { start: number; end: number } | null;
  onChange: (value: { start: number; end: number } | null) => void;
  replacedTextMatch?: string;
}

const getValueToTakeForLabel = (
  replacedTextMatch: string | undefined,
  value: { start: number; end: number } | null
) => {
  if (value) {
    if (replacedTextMatch === '[StartTime]') {
      return value?.start;
    }
    if (replacedTextMatch === '[EndTime]') {
      return value?.end;
    }
  }
  return null;
};

const TimerStartEndDropdownInput: FC<Props> = ({
  value,
  onChange,
  onCancel,
  disabled,
  onlyLabel,
  replacedTextMatch
}) => {
  const [opened, setOpened] = useState(false);
  const [startTimer, setStartTimer] = useState(value?.start ?? null);
  const [endTimer, setEndTimer] = useState(value?.end ?? null);

  useEffect(() => {
    setStartTimer(value?.start ?? null);
    setEndTimer(value?.end ?? null);
  }, [value]);

  const [t] = useTranslation();

  const handleCancel = () => {
    setOpened(false);
    onCancel();
  };

  const handleConfirm = () => {
    if (startTimer !== null && endTimer !== null) {
      onChange({ start: startTimer, end: endTimer });
    }
    setOpened(false);
  };

  const valueForLabel = getValueToTakeForLabel(replacedTextMatch, value);

  if (onlyLabel) {
    return <>{valueForLabel ? formatTimer(valueForLabel) : 'XX:XX'}</>;
  }

  const incoherenceError = startTimer !== null && endTimer !== null ? startTimer > endTimer : false;
  const timeWindowError =
    startTimer !== null && endTimer !== null
      ? Math.max(endTimer - startTimer, 0) > MICROMARKET_TIME_WINDOW_MAX_IN_S
      : false;

  return (
    <Dropdown
      label={valueForLabel ? formatTimer(valueForLabel) : 'XX:XX'}
      dropdownClassName={style.portal}
      withIcon={false}
      opened={opened}
      onOpenChange={setOpened}
      disabled={disabled}
      variation="inline"
      width={230}
    >
      <div className={style.timers}>
        <InputTime
          onChange={setStartTimer}
          value={startTimer}
          title={t('component-timer_start_end_dropdown-start')}
          exampleDuration="11:00"
        />
        <InputTime
          onChange={setEndTimer}
          value={endTimer}
          title={t('component-timer_start_end_dropdown-end')}
          exampleDuration="12:00"
        />
      </div>
      <div className={style.errors}>
        {startTimer !== null && endTimer !== null && (
          <>
            {incoherenceError && (
              <span>{t('component-timer_start_end_dropdown-error-incoherence')}</span>
            )}
            {timeWindowError && (
              <span>{t('component-timer_start_end_dropdown-error-time_window')}</span>
            )}
          </>
        )}
      </div>
      <div className={style.footer}>
        <Button onClick={handleCancel} variation="cancel" compact>
          {t('common-cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variation="secondary"
          compact
          disabled={startTimer === null || endTimer === null || incoherenceError || timeWindowError}
        >
          {t('common-save')}
        </Button>
      </div>
    </Dropdown>
  );
};

const ControlledTimerStartEndDropdownInput: FC<Omit<Props, 'onCancel'>> = ({
  onChange,
  value,
  ...props
}) => {
  const [timerValue, setTimerValue] = useState<{ start: number; end: number } | null>(
    value ?? { start: 0, end: 0 }
  );

  const handleConfirm = (newValue: { start: number; end: number } | null) => {
    setTimerValue(newValue);
    onChange(newValue);
  };

  useEffect(() => {
    setTimerValue(value);
  }, [value]);

  return (
    <TimerStartEndDropdownInput
      {...props}
      value={timerValue}
      onCancel={() => setTimerValue(null)}
      onChange={handleConfirm}
    />
  );
};

export { TimerStartEndDropdownInput, ControlledTimerStartEndDropdownInput };
